import { useContext, useMemo, useState } from 'react';
import Image from 'next/image';
import { Link } from '~/components/global';
import { useInterval } from 'usehooks-ts';
import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import useUser from '~/lib/user';
import PlusLogo from '../../../public/as-plus-2.svg';
import ShopExclusives from '../../../public/shop-exclusives.svg';
import CommentDots from '../../../public/comment-dots.svg';
import Refresh from '../../../public/refresh.svg';
import { useRouter } from 'next/router';
import { SessionObjectProps } from '~/types/user';
import { getPlusDiscount } from '~/lib/util/helpers';
import { GlobalContext } from '~/contexts/global';

dayjs.extend(relativeTime);

interface CampaignTimerButtonProps {
  label: string;
  href: string;
  endDate: string;
}

function AskSumo() {
  const router = useRouter();
  const {
    campaigns: { activeAutoCampaign },
  } = useContext(GlobalContext);
  const { user } = useUser();
  const isNewBuyer = useMemo(
    () => !(user?.number_of_purchases && user?.number_of_purchases > 0),
    [user],
  );
  const hasAskedQuestion = useMemo(
    () => router.pathname === '/asksumo' && !!router.query.question,
    [router.pathname, router.query.question],
  );

  const showAskSumoDiscount =
    activeAutoCampaign?.campaign_config?.allow_asksumo_discount;

  return (
    <Link
      href="/asksumo/?ref=asksumo-navitem"
      className="flex items-center justify-center whitespace-nowrap rounded-[40px] bg-gradient-to-r from-[#BCF2CB] to-[#AEDBF4] px-5 py-1"
    >
      <Image
        src={hasAskedQuestion ? Refresh : CommentDots}
        alt="Ask Sumo"
        className="mr-[5px] align-middle"
      />
      {hasAskedQuestion ? (
        <>
          <strong>AskSumo</strong>{' '}
          <span className="md:max-lg:hidden">&nbsp;a new question</span>
        </>
      ) : isNewBuyer && !showAskSumoDiscount ? (
        <>
          <strong>AskSumo</strong>{' '}
          <span className="md:max-lg:hidden">&nbsp;and get 10% off</span>
        </>
      ) : (
        <>
          <span className="md:max-lg:hidden">
            Introducing&nbsp;<strong>AskSumo</strong>, try it now
          </span>
          <span className="max-md:hidden lg:hidden">
            Try <strong>AskSumo</strong>
          </span>
        </>
      )}
    </Link>
  );
}

function PlusDiscount({ session }: { session?: SessionObjectProps }) {
  return (
    <Link
      href="/plus/?ref=Plusnav"
      className="flex items-center justify-center rounded-[40px] bg-dorado-light px-5 py-1 text-black-pearl"
    >
      <Image src={PlusLogo} alt="Save 10%" className="mr-[5px] align-middle" />
      <strong className="whitespace-nowrap">
        Save {getPlusDiscount(session)}%
      </strong>
    </Link>
  );
}

function ShopPlusExclusives() {
  return (
    <Link
      href="/collections/plus-exclusives/?ref=PEnav"
      className="flex items-center justify-center whitespace-nowrap rounded-[40px] bg-bolt/20 px-5 py-1"
    >
      <Image
        src={ShopExclusives}
        alt="Shop Plus exclusives"
        className="mr-[5px] h-6 align-middle"
      />
      <strong className="">Shop Plus exclusives</strong>
    </Link>
  );
}

function CampaignTimerButton({
  label,
  href,
  endDate,
}: CampaignTimerButtonProps) {
  const [now, setNow] = useState<Dayjs>(dayjs());

  useInterval(() => {
    setNow(dayjs());
  }, 1000);

  const timeRemaining = useMemo(() => {
    const end = dayjs(endDate);
    let seconds = end.diff(now, 'second');
    if (seconds > 0) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor(seconds / 60) % 60;
      seconds %= 60;
      const parts = [];
      if (hours) {
        parts.push(String(hours).padStart(2, '0'));
      }
      parts.push(String(mins).padStart(2, '0'));
      if (hours || mins || seconds) {
        parts.push(String(seconds).padStart(2, '0'));
      }
      return parts.join(':');
    }

    return '00:00';
  }, [endDate, now]);

  return (
    <Link
      href={`${href}`}
      className="flex items-center justify-center rounded-[40px] bg-bolt/20 px-5 py-1"
    >
      <Image
        src={ShopExclusives}
        alt={label}
        className="mr-[5px] align-middle"
      />
      <strong className="">{label}</strong>
      <span className="ml-1.5">{timeRemaining}</span>
    </Link>
  );
}

export default function NavPromo() {
  const { session, user, isLoading: isLoadingUser } = useUser();
  const { button } = session || {};

  const isCampaign = useMemo(() => {
    if (button?.end_date) {
      const now = dayjs();
      const ends = dayjs(button.end_date);
      return now < ends;
    }

    return false;
  }, [button?.end_date]);

  const hasUser = useMemo(() => user && user.is_authenticated, [user]);
  const hasPlus = useMemo(() => user && user.has_plus, [user]);

  if (isLoadingUser) {
    return <div />;
  }

  let buttonEl = null;
  if (isCampaign && button) {
    // campaign button overrides all other buttons
    buttonEl = (
      <CampaignTimerButton
        label={button.label}
        href={button.link}
        endDate={button.end_date}
      />
    );
  } else if (!hasUser) {
    // if anonymous, show askSumo button
    buttonEl = <AskSumo />;
  } else if (hasUser && !hasPlus) {
    // if user is not a plus member, show save %off button (now dynamic)
    buttonEl = <PlusDiscount session={session} />;
  } else if (hasUser && hasPlus) {
    // if user is a plus member, show shop plus exclusives button
    buttonEl = <ShopPlusExclusives />;
  }

  // As long the AskSumo navItem is present we need to hide the pill between 768px and 1024px
  // to avoid overlapping with the nav items, using the class `md:max-lg:hidden`
  // see components/navigation/components/NavGroups.tsx for AskSumo nav item.
  return (
    <div className="my-6 w-full md:my-0 md:w-auto md:max-lg:hidden">
      {buttonEl}
    </div>
  );
}
